import { CSSProperties, useId } from 'react';
import { IconName } from '@shared/components/Icon';
import styles from './AskMark.module.css';

interface Props {
  /** When true, the stars twinkle (used in the waiting/thinking state) and render green. */
  animated?: boolean;
  /** When true, the stars fill with the Lighthouse "Green Gradient" instead of a flat colour. */
  gradient?: boolean;
  size?: number;
}

/**
 * The Ask mark — the one asset every Ask surface identifies itself with: the Lighthouse
 * `sparkle-ai`. Everything that shows it goes through this module, so swapping the mark is an
 * edit here and nowhere else.
 *
 * It exists in three forms because the surfaces need different things, and all of them have to
 * change together:
 *
 * - `AskMark`, below: the standalone mark, the only form that animates or takes the gradient.
 * - `AskMenuMark`: the gradient mark sized and slotted for a `Menu.Item`'s `startContent`. A menu
 *   row has no button behind its icon to carry the gradient, so the mark carries it instead.
 * - `ASK_MARK_ICON`: the `Icon` name, for anything taking an `icon` or `name` prop (buttons,
 *   `IconAvatar`). Buttons should be an `AskButton`, which puts the gradient behind it.
 */
export const ASK_MARK_ICON: IconName = 'sparkle-ai';

/**
 * Size for the mark sitting inline with body copy — the thinking indicator, the tool-call
 * lines, the replace warning. Generous, because the glyph has transparent margin inside its own
 * viewBox, so it reads smaller than it measures.
 */
export const ASK_MARK_INLINE_SIZE = 40;

/** Matches `RowContent`'s default icon size, so the mark lines up with the other menu icons. */
const MENU_MARK_SIZE = 24;

/**
 * The mark as its own element (a large and a small four-point star). Static and Coast-purple by
 * default; when `animated`, it turns Coast-green and each star twinkles on a staggered,
 * infrequent loop (`transform-box: fill-box` scales each about its own center). Honors
 * `prefers-reduced-motion`.
 *
 * Drawn inline rather than through `Icon` because the animation addresses each star
 * separately, and the gradient needs its definition in the document.
 */
export function AskMark({ animated = false, gradient = false, size = 86 }: Props) {
  const style: CSSProperties = { width: size, height: size };
  // `useId` returns colons, which don't survive inside `url(#…)` in every browser.
  const gradientId = `ask-mark-gradient-${useId().replace(/:/g, '')}`;
  const fill = gradient ? `url(#${gradientId})` : 'currentColor';
  return (
    <svg
      className={[styles.mark, animated && styles.animated].filter(Boolean).join(' ')}
      style={style}
      viewBox="-3 -3 30 30"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      {gradient && (
        <defs>
          {/* The CSS `radial-gradient(217.54% 142.97% at 100% -7.98%, …)` from the Figma style,
              mapped onto the glyph's 24-unit frame: centered just above its top-right corner. */}
          <radialGradient
            id={gradientId}
            cx="0"
            cy="0"
            r="1"
            gradientUnits="userSpaceOnUse"
            gradientTransform="translate(24 -1.9152) scale(52.2096 34.3128)"
          >
            <stop offset="0.2452" className={styles.gradientStart} />
            <stop offset="0.9323" className={styles.gradientEnd} />
          </radialGradient>
        </defs>
      )}
      <g className={styles.star}>
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M11.5367 2.75429C11.7056 2.33849 12.2943 2.3385 12.4632 2.75429L13.7286 5.86983C14.5414 7.87105 16.1289 9.45856 18.1302 10.2714L21.2457 11.5368C21.6615 11.7057 21.6615 12.2944 21.2457 12.4633L18.1302 13.7287C16.1289 14.5415 14.5414 16.129 13.7286 18.1302L12.4632 21.2458C12.2943 21.6616 11.7056 21.6616 11.5367 21.2458L10.2713 18.1302C9.4585 16.129 7.87099 14.5415 5.86978 13.7287L2.75423 12.4633C2.33843 12.2944 2.33843 11.7057 2.75423 11.5368L5.86978 10.2714C7.87099 9.45856 9.4585 7.87106 10.2713 5.86985L11.5367 2.75429Z"
          fill={fill}
        />
      </g>
      <g className={[styles.star, styles.delayed].join(' ')}>
        <path
          d="M5.91789 3.60756C5.68188 3.50641 5.49382 3.31835 5.39267 3.08233L4.95981 2.07233C4.78664 1.66827 4.21383 1.66827 4.04066 2.07233L3.6078 3.08233C3.50665 3.31835 3.31859 3.50641 3.08258 3.60756L2.07257 4.04042C1.66852 4.21358 1.66852 4.7864 2.07257 4.95956L3.08258 5.39242C3.31859 5.49357 3.50665 5.68163 3.6078 5.91765L4.04066 6.92766C4.21383 7.33171 4.78664 7.33171 4.95981 6.92766L5.39267 5.91765C5.49382 5.68163 5.68188 5.49357 5.91789 5.39242L6.9279 4.95956C7.33195 4.7864 7.33195 4.21358 6.9279 4.04042L5.91789 3.60756Z"
          fill={fill}
        />
      </g>
    </svg>
  );
}

/**
 * The gradient mark for a `Menu.Item`, passed as `startContent` in place of `icon`. Wrapped in a
 * slot the same width as `RowContent`'s icon slot, so the label lines up with the rows around it.
 */
export function AskMenuMark() {
  return (
    <span className={styles.menuSlot}>
      <AskMark gradient size={MENU_MARK_SIZE} />
    </span>
  );
}
